import { useState, type ChangeEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FormControl, InputLabel, FilledInput, FormHelperText, InputAdornment, IconButton } from '@mui/material';

type PasswordFieldBaseProps = {
  name: string;
  labelKey: string;
  autoComplete?: string;
  autoFocus?: boolean;
  error?: string;
  disabled?: boolean;
};

type PasswordFieldProps = PasswordFieldBaseProps &
  (
    | {
        value: string;
        onChange: ChangeEventHandler<HTMLInputElement>;
      }
    | {
        value?: undefined;
        onChange?: ChangeEventHandler<HTMLInputElement>;
      }
  );

export function PasswordField({
  name,
  labelKey,
  autoComplete,
  autoFocus = false,
  value,
  error,
  disabled = false,
  onChange,
}: PasswordFieldProps) {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { t } = useTranslation();
  const errorId = `${name}-error-text`;

  function handleClickShowPassword() {
    setShowPassword(!showPassword);
  }

  return (
    <FormControl
      variant="filled"
      margin="normal"
      fullWidth
      required
      autoFocus={autoFocus}
      error={error !== undefined}
      disabled={disabled}
      sx={{
        backgroundColor: 'primary.main',
        borderRadius: 'var(--mui-shape-borderRadius)',
        '& .MuiFormHelperText-root': {
          backgroundColor: '#fff',
          marginLeft: 0,
          marginRight: 0,
          paddingLeft: '14px',
          paddingRight: '14px',
          borderEndEndRadius: '3px',
          borderEndStartRadius: '3px',
          boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
        },
        '& .Mui-error::before': {
          borderWidth: 'thick',
        },
      }}
    >
      <InputLabel sx={{ color: 'white', fontWeight: 'bold' }} htmlFor={name} style={{ color: 'white' }} shrink={true}>
        {t(labelKey)}
      </InputLabel>
      <FilledInput
        id={name}
        name={name}
        type={showPassword ? 'text' : 'password'}
        autoComplete={autoComplete}
        aria-describedby={error !== undefined ? errorId : undefined}
        value={value}
        onChange={onChange}
        sx={{ color: 'white' }}
        inputProps={{ 'data-qa-element-id': `login-${name}-input` }}
        data-qa-element-id={`login-${name}-div`}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              type="button"
              aria-label={t(showPassword ? 'login.hidePassword' : 'login.showPassword')}
              onClick={handleClickShowPassword}
            >
              {showPassword ? (
                <Visibility sx={{ color: 'white' }} fontSize="large" />
              ) : (
                <VisibilityOff sx={{ color: 'white' }} fontSize="large" />
              )}
            </IconButton>
          </InputAdornment>
        }
      />
      <FormHelperText id={errorId} data-qa-element-id={`changePassword-${name}-error-text`}>
        {error}
      </FormHelperText>
    </FormControl>
  );
}
